"use client";

import { usePostHog } from "posthog-js/react";
import { useMemo } from "react";

/**
 * Ploy tracking plan.
 *
 * Naming convention: `object_action`, lowercase snake_case. Context belongs in
 * properties, never in the event name — prefer `cta_clicked { location: "hero" }`
 * over `hero_cta_clicked` so funnels can group across surfaces.
 *
 * Every event declares its properties here. Adding a property to an existing
 * event is safe; renaming an event breaks saved insights, so treat these names
 * as a public contract.
 */

/** Marketing surface a call to action was rendered on. */
export type CtaLocation =
	| "hero"
	| "navbar"
	| "footer"
	| "cta_section"
	| "pricing"
	| "comparison"
	| "features"
	| "blog"
	| "changelog";

/** Well-known outbound destinations, so link clicks group cleanly. */
export type OutboundDestination =
	| "docs"
	| "github"
	| "discord"
	| "x"
	| "email"
	| "external";

/** Authentication method used for signup/login. */
export type AuthMethod = "email" | "github" | "passkey";

export type BillingCycle = "monthly" | "annual";

export interface AnalyticsEventMap {
	// ---------------------------------------------------------------- marketing
	/** Any primary/secondary call to action on the marketing site. */
	cta_clicked: {
		cta_id: string;
		cta_text: string;
		location: CtaLocation;
		destination?: string;
	};
	/** Click leaving the marketing site (docs, GitHub, Discord, socials, mailto). */
	outbound_link_clicked: {
		destination: OutboundDestination;
		link_url: string;
		link_text?: string;
		location: CtaLocation;
	};
	/** Pricing section scrolled into view. Fires once per page load. */
	pricing_viewed: {
		source: string;
		billing_cycle: BillingCycle;
	};
	billing_cycle_toggled: {
		billing_cycle: BillingCycle;
	};
	/** A pricing plan CTA was clicked, before any redirect to checkout/signup. */
	plan_selected: {
		plan_name: string;
		billing_cycle: BillingCycle;
		cta_text: string;
		is_authenticated: boolean;
	};
	faq_expanded: {
		question: string;
		position: number;
	};
	comparison_viewed: {
		competitor: string;
	};
	blog_post_viewed: {
		slug: string;
		title: string;
		category?: string;
	};
	changelog_entry_viewed: {
		slug: string;
	};
	feature_page_viewed: {
		slug: string;
	};

	// -------------------------------------------------------------------- auth
	/** User submitted the signup form / started an OAuth signup. */
	signup_started: {
		method: AuthMethod;
		source: string;
	};
	signup_completed: {
		method: AuthMethod;
	};
	signup_failed: {
		method: AuthMethod;
		error_type: string;
	};
	login_started: {
		method: AuthMethod;
	};
	login_completed: {
		method: AuthMethod;
	};
	login_failed: {
		method: AuthMethod;
		error_type: string;
	};
	logout_completed: {
		location: string;
	};

	// -------------------------------------------------------------- onboarding
	onboarding_started: {
		step_name: string;
	};
	onboarding_step_completed: {
		step_number: number;
		step_name: string;
	};
	onboarding_step_skipped: {
		step_number: number;
		step_name: string;
		context: string;
	};
	onboarding_completed: {
		steps_completed: number;
	};
	github_oauth_connected: {
		context: string;
	};
	github_app_install_started: {
		context: string;
	};
	github_app_install_completed: {
		context: string;
	};
	github_app_configure_clicked: {
		context: string;
	};
	repositories_refreshed: {
		context: string;
	};
	deploy_button_repository_started: {
		source_repository: string;
	};
	deploy_button_repository_created: {
		source_repository: string;
		target_repository: string;
	};
	deploy_button_deployment_started: {
		source_repository: string;
		target_repository: string;
	};
	deploy_button_deployment_created: {
		project_id: string | null;
		deployment_id: string | null;
	};

	// ------------------------------------------------------------ core product
	repository_connected: {
		context: string;
		is_private?: boolean;
		template?: string | null;
	};
	project_created: {
		context: string;
		project_kind?: string;
		project_id?: string;
	};
	project_deleted: {
		project_id: string;
	};

	// ------------------------------------------------------------- migration
	/** A Vercel token was accepted and the user's projects were listed. */
	vercel_account_connected: {
		project_count: number;
		team_count: number;
	};
	vercel_migration_started: {
		framework: string;
		support: string;
	};
	vercel_migration_completed: {
		framework: string;
		project_id: string;
		environment_variables_imported: number;
	};
	vercel_migration_failed: {
		framework: string;
		error_message: string;
	};
	project_moved: {
		project_id: string;
	};
	organization_created: {
		context: string;
	};
	/** Aha moment: a deployment was kicked off from the dashboard. */
	deployment_triggered: {
		project_id: string;
		branch?: string;
		source: "manual" | "redeploy";
		clear_cache?: boolean;
	};
	deployment_cancelled: {
		project_id: string;
		deployment_id: string;
	};
	environment_variable_created: {
		project_id: string;
		is_secret: boolean;
	};
	environment_variable_updated: {
		project_id: string;
	};
	environment_variable_deleted: {
		project_id: string;
	};
	custom_domain_added: {
		scope: "project" | "organization";
	};
	custom_domain_removed: {
		scope: "project" | "organization";
	};
	dns_record_created: {
		record_type: string;
	};
	database_query_executed: {
		project_id: string;
		is_read_only: boolean;
	};

	// --------------------------------------------------------------- team/orgs
	team_member_invited: {
		role: string;
	};
	team_member_removed: {
		role: string;
	};
	team_invite_accepted: {
		role?: string;
	};

	// ------------------------------------------------------------ monetization
	/** User is being sent to Stripe Checkout. */
	checkout_started: {
		plan: string;
		billing_cycle: BillingCycle;
		location: "pricing" | "billing_settings";
	};
	checkout_failed: {
		plan: string;
		error_type: string;
	};
	billing_portal_opened: {
		plan: string;
	};
	subscription_cancelled: {
		plan: string;
	};
	subscription_resumed: {
		plan: string;
	};
}

export type AnalyticsEventName = keyof AnalyticsEventMap;

export interface Analytics {
	capture: <T extends AnalyticsEventName>(
		event: T,
		properties: AnalyticsEventMap[T],
	) => void;
	identify: (
		userId: string,
		properties?: Record<string, string | number | boolean | null | undefined>,
	) => void;
	reset: () => void;
}

/**
 * Typed wrapper around `usePostHog`. Use this instead of calling
 * `posthog.capture` directly so event names and properties stay consistent.
 */
export function useAnalytics(): Analytics {
	const posthog = usePostHog();

	return useMemo(
		() => ({
			capture: (event, properties) => {
				posthog.capture(event, properties);
			},
			identify: (userId, properties) => {
				posthog.identify(userId, properties);
			},
			reset: () => {
				posthog.reset();
			},
		}),
		[posthog],
	);
}
